import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { HistoryTerm, Suggestion } from '~app/modules/generic/models';
import { Product } from '~shared/models';
import { MemoizedSelector, select, Store } from '@ngrx/store';
import {
  BuyerState,
  CategorySelect,
  FocusOnSearch,
  getSearchQuery,
  getSearchSuggestionsHistoryTerms,
  getSearchSuggestionsItems,
  getSearchSuggestionsProducts,
  HideSuggestionsList,
  isSearchSuggestionsListVisible,
  RemoveSearchHistoryTerm,
  SearchFromHeaderBar,
  StartSearch,
  UpdateSearchQuery,
} from '~app/modules/generic/store';
import { Subject } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { MobileMenuService } from '~app/modules/generic/services/mobile-menu.service';
import { SuggestionTermUtilService } from '~app/modules/generic/services/suggestion-term-util.service';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { SuggestionApplicationEventsHandler } from '~app/modules/generic/classes';
import { CategoriesService } from '~app/modules/generic/services/categories.service';
import { Observable } from 'rxjs/internal/Observable';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { NUMBER_1 } from '~shared/constants';
import { getQueryParams, RouterStateUrl } from '~core/store';
import { Params } from '@angular/router';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { BlackDealsFeatureFlagService } from '~shared/services/black-deals-feature-flag.service';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';

@Component({
  selector: 'app-suggestions-list',
  templateUrl: './suggestions-list.component.html',
})
export class SuggestionsListComponent implements OnDestroy, OnInit {
  @Input() isMobile: boolean;

  historyTerms: HistoryTerm[] = [];
  suggestions: Suggestion[] = [];
  suggestedProducts: Product[] = [];
  searchQuery: string;
  isSuggestionsListVisible$: Observable<boolean>;

  private readonly destroySubject = new Subject<boolean>();
  private suggestionApplicationEventsHandler: SuggestionApplicationEventsHandler;

  constructor(
    private store: Store<BuyerState>,
    private changeDetectionRef: ChangeDetectorRef,
    private mobileMenuService: MobileMenuService,
    private applicationEventService: ApplicationEventService,
    private suggestionTermUtilService: SuggestionTermUtilService,
    private categoriesService: CategoriesService,
    private routerStore: Store<RouterStateUrl>,
    private blackDealsFeatureFlagService: BlackDealsFeatureFlagService,
    private navigationHelper: NavigationHelper,
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService
  ) {
    this.suggestionApplicationEventsHandler = new SuggestionApplicationEventsHandler(
      this.applicationEventService,
      this.blackDealsFeatureFlagService,
      this.navigationHelper
    );
  }

  ngOnInit(): void {
    this.initSuggestions();
    this.initSuggestionsProducts();
    this.initHistoryTerms();

    this.isSuggestionsListVisible$ = this.store.pipe(select(isSearchSuggestionsListVisible));

    this.store.pipe(select(getSearchQuery), takeUntil(this.destroySubject)).subscribe((searchQuery) => {
      this.searchQuery = searchQuery;
    });
  }

  ngOnDestroy() {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  onPromoLabelClick() {
    this.suggestionApplicationEventsHandler.fireSuggestionAllProductsSelected(this.searchQuery);
    this.store.dispatch(new HideSuggestionsList());
    this.mobileMenuService.close();
  }

  onTermSelected(term: Suggestion | HistoryTerm) {
    this.suggestionApplicationEventsHandler.fireSuggestionsSelected(term, this.searchQuery);
    if (this.suggestionTermUtilService.isText(term)) {
      let pageNumberWhenUserSearchedFromHeaderBar = NUMBER_1;

      this.routerStore
        .pipe(select(getQueryParams), take(1), takeUntil(this.destroySubject))
        .subscribe((params: Params) => {
          const offset = params.offset;
          const limit = params.limit;

          if (offset && limit) {
            pageNumberWhenUserSearchedFromHeaderBar = UrlUtils.getCurrentPage(offset, limit);
          }
          this.store.dispatch(
            new SearchFromHeaderBar({
              isSearchFromHeaderBar: true,
              pageNumberWhenUserSearchedFromHeaderBar,
            })
          );
          this.store.dispatch(new StartSearch(term));
        });
      this.store.dispatch(new UpdateSearchQuery(term.value));
    } else if (this.suggestionTermUtilService.isCategory(term)) {
      const category = this.suggestionTermUtilService.toCategoryObject(term);
      this.store.dispatch(new CategorySelect(category));
      this.store.dispatch(new UpdateSearchQuery(''));
    }
    this.store.dispatch(new HideSuggestionsList());
  }

  onHistoryRemoved(value: HistoryTerm) {
    this.store.dispatch(new RemoveSearchHistoryTerm(value));
    if (this.historyTerms && this.historyTerms.length === 0) {
      this.store.dispatch(new HideSuggestionsList());
    }

    this.store.dispatch(new FocusOnSearch(true));
  }

  onProductSelected(product: Product, priceType: PriceTypes) {
    this.suggestionApplicationEventsHandler.fireProductSelected(product, priceType, this.searchQuery);
    this.store.dispatch(new HideSuggestionsList());
  }

  private initSuggestions(): void {
    this.getArrayFromStoreAndDetectChanges(getSearchSuggestionsItems)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((suggestions: Suggestion[] = []) => this.onSuggestions(suggestions));
  }

  private initSuggestionsProducts(): void {
    this.getArrayFromStoreAndDetectChanges(getSearchSuggestionsProducts)
      .pipe(takeUntil(this.destroySubject))
      .subscribe((products: Product[] = []) => {
        this.suggestedProducts = products.slice();
        this.suggestionApplicationEventsHandler.setSuggestionsProducts(this.suggestedProducts);
      });
  }

  private initHistoryTerms(): void {
    this.getArrayFromStoreAndDetectChanges(getSearchSuggestionsHistoryTerms)
      .pipe(
        map((historyTerms) => historyTerms.filter((term) => term && term.value && term.value.length > 0)),
        takeUntil(this.destroySubject)
      )
      .subscribe((historyTerms = []) => {
        this.historyTerms = historyTerms.slice();
      });
  }

  private getArrayFromStoreAndDetectChanges(selector: MemoizedSelector<object, any>) {
    return this.store.pipe(
      select(selector),
      map((array) => (Array.isArray(array) ? array : [])),
      tap(() => this.changeDetectionRef.detectChanges())
    );
  }

  private onSuggestions(suggestions: Suggestion[]): void {
    this.suggestions = [...suggestions];
    this.suggestionApplicationEventsHandler.setSuggestions(this.suggestions);
  }
}
